/* <rr-header> — estado is-scrolled vía IntersectionObserver (sin listeners
   de scroll) para el swap de logo y el fondo del modo overlay, y cierre
   del drawer con Escape o al navegar. También publica su propia altura
   real como --rr-header-h en :root, para que cualquier elemento sticky
   (la barra de filtros de colección) se enganche debajo sin adivinar un
   número fijo: la altura cambia con el ancho y con el idioma (con el swap
   de logo no: header.liquid reserva la del logo más alto).
   Y le cede el sitio a esa barra cuando se pega arriba (relay()). */
(() => {
  if (customElements.get('rr-header')) return;

  class RRHeader extends HTMLElement {
    connectedCallback() {
      const setHeight = () => {
        document.documentElement.style.setProperty('--rr-header-h', `${this.offsetHeight}px`);
      };
      setHeight();
      new ResizeObserver(setHeight).observe(this);

      const sentinel = document.createElement('div');
      sentinel.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:140px;pointer-events:none;visibility:hidden;';
      document.body.prepend(sentinel);
      new IntersectionObserver((entries) => {
        entries.forEach((en) => this.classList.toggle('is-scrolled', !en.isIntersecting));
      }).observe(sentinel);

      this.relay();

      // El carrito se nombra por su aria-label ("Cart (2)"): en móvil solo se ve la
      // bolsa. Al añadir (rr-atc) o cambiar cantidades (drawer, carrito) se pone al día.
      const cart = this.querySelector('[data-rr-cart-label]');
      if (cart) {
        const label = (e) => {
          const n = e.detail && e.detail.cart && e.detail.cart.item_count;
          if (typeof n === 'number') cart.setAttribute('aria-label', cart.dataset.rrCartLabel.replace('§', n));
        };
        document.addEventListener('rr:cart-updated', label);
        document.addEventListener('rr:cart-changed', label);
      }

      const burger = this.querySelector('[data-rr-burger]');
      if (!burger) return;
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') burger.removeAttribute('open');
      });
      burger.addEventListener('click', (e) => {
        if (e.target.closest('a')) burger.removeAttribute('open');
      });
    }

    /* Relevo del sticky: una barra con data-rr-header-relay (los filtros de
       colección) toma el sitio del header. En cuanto se pega debajo de él,
       html.rr-header-away aparta el header y la barra sube a top: 0; al
       volver a subir por encima de la barra, el header regresa. Un marcador
       de 1 px justo antes de la barra señala su sitio natural y el observer
       recorta de la raíz la altura del header.
       La raíz se alarga además hacia abajo sin límite práctico: así el
       marcador solo cambia de estado al cruzar el borde inferior del header.
       Con la raíz del alto de la pantalla, un salto de scroll (volver atrás
       a una colección ya bajada, subir de golpe) lo llevaba de debajo de la
       pantalla a encima sin cortarla nunca: el observer no avisaba y la
       zona se quedaba como estaba (header visible con la barra pegada, o
       escondido arriba del todo con el listener de scroll aún puesto).
       Dentro de esa zona el header también asoma en cuanto se sube un poco
       (como Zara o ASOS), para tener el menú a mano en mitad de la rejilla,
       y se vuelve a ir al bajar. Para saber hacia dónde se va hace falta un
       listener de scroll: pasivo, una lectura por frame y solo mientras se
       está en la zona. Solo en modo solid (en overlay el envoltorio mide 0 y
       su translate no apartaría al header) y fuera del editor. */
    relay() {
      const bar = document.querySelector('[data-rr-header-relay]');
      if (!bar || !this.classList.contains('rr-header--solid')) return;
      if (window.Shopify && window.Shopify.designMode) return;

      const mark = document.createElement('div');
      mark.setAttribute('aria-hidden', 'true');
      mark.style.cssText = 'height:1px;margin-bottom:-1px;pointer-events:none;visibility:hidden;';
      bar.before(mark);

      const root = document.documentElement;
      let zone = false;
      let lastY = 0;
      let ticking = false;

      const onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          const y = window.scrollY;
          // el rebote de iOS al llegar arriba o abajo no cuenta como subir o bajar
          if (y < 0 || y > root.scrollHeight - window.innerHeight) return;
          // 8 px de margen, para que un temblor del dedo no lo haga parpadear
          if (Math.abs(y - lastY) < 8) return;
          root.classList.toggle('rr-header-away', y > lastY);
          lastY = y;
        });
      };
      const setZone = (inZone) => {
        if (inZone === zone) return;
        zone = inZone;
        lastY = window.scrollY;
        root.classList.toggle('rr-header-away', inZone);
        if (inZone) window.addEventListener('scroll', onScroll, { passive: true });
        else window.removeEventListener('scroll', onScroll);
      };

      let io;
      let h = -1;
      const watch = () => {
        if (this.offsetHeight === h) return;
        h = this.offsetHeight;
        if (io) io.disconnect();
        io = new IntersectionObserver(([en]) => {
          const top = en.rootBounds ? en.rootBounds.top : h;
          setZone(!en.isIntersecting && en.boundingClientRect.top < top);
        }, { rootMargin: `-${h}px 0px 100000px 0px` });
        io.observe(mark);
      };
      watch();
      new ResizeObserver(watch).observe(this);
    }
  }

  customElements.define('rr-header', RRHeader);
})();
